import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { useCounter } from "@/components/demo/lib/useCounter";
import { useReveal } from "@/components/demo/lib/useReveal";

const WA = "41765008176";

function Aurora() {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-1/3 -left-1/4 h-[120vh] w-[120vh] rounded-full opacity-40 blur-[120px]" style={{ background: "radial-gradient(circle, #7c3aed 0%, transparent 60%)", animation: "drift1 22s ease-in-out infinite alternate" }} />
      <div className="absolute -top-1/4 right-0 h-[100vh] w-[100vh] rounded-full opacity-40 blur-[120px]" style={{ background: "radial-gradient(circle, #06b6d4 0%, transparent 60%)", animation: "drift2 28s ease-in-out infinite alternate" }} />
      <div className="absolute bottom-0 left-1/3 h-[80vh] w-[80vh] rounded-full opacity-30 blur-[120px]" style={{ background: "radial-gradient(circle, #ec4899 0%, transparent 60%)", animation: "drift3 26s ease-in-out infinite alternate" }} />
    </div>
  );
}

function LiveMetric({ v, suffix, label }: { v: number; suffix: string; label: string }) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const c = useCounter(v, visible);
  return (
    <div ref={ref}>
      <div className="font-semibold text-4xl sm:text-5xl tracking-tight tabular-nums bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">
        {Math.round(c).toLocaleString("it-CH")}{suffix}
      </div>
      <div className="text-xs text-white/50 mt-2 tracking-wide">{label}</div>
    </div>
  );
}

function FlashWebUltimate() {
  const [annual, setAnnual] = useState(true);
  const [tab, setTab] = useState<"requests" | "agents" | "analytics">("requests");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [scroll, setScroll] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setScroll(max > 0 ? (h.scrollTop || document.body.scrollTop) / max : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#05060a] text-white antialiased relative overflow-x-hidden">
      <style>{`
        body { font-family: 'Inter', sans-serif }
        @keyframes drift1 { 0% { transform: translate(0,0) scale(1) } 100% { transform: translate(15%, 8%) scale(1.15) } }
        @keyframes drift2 { 0% { transform: translate(0,0) scale(1) } 100% { transform: translate(-10%, 12%) scale(1.1) } }
        @keyframes drift3 { 0% { transform: translate(0,0) scale(1) } 100% { transform: translate(8%, -10%) scale(1.2) } }
        .grid-bg { background-image: linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px); background-size: 64px 64px; }
        .grain { position: relative; }
        .grain::after { content: ""; position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px); background-size: 3px 3px; pointer-events: none; }
      `}</style>

      {/* SCROLL PROGRESS */}
      <div className="fixed top-0 inset-x-0 h-0.5 bg-white/5 z-[60]">
        <div className="h-full bg-gradient-to-r from-violet-400 via-cyan-400 to-pink-400 transition-[width]" style={{ width: `${scroll * 100}%` }} />
      </div>

      {/* NAV */}
      <header className="fixed top-3 inset-x-3 z-50 max-w-6xl mx-auto">
        <div className="backdrop-blur-xl bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 grid place-items-center text-xs font-bold">N</div>
            <div className="font-semibold tracking-tight">Nebula <span className="text-white/40 font-normal">AI</span></div>
          </div>
          <nav className="hidden md:flex gap-7 text-sm text-white/60">
            <a href="#product" className="hover:text-white">Piattaforma</a>
            <a href="#demo" className="hover:text-white">Demo live</a>
            <a href="#pricing" className="hover:text-white">Prezzi</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
          </nav>
          <a href="#contact" className="text-sm bg-white text-black px-4 py-1.5 rounded-lg font-medium hover:bg-white/90 transition">Richiedi una demo</a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-40 pb-32 px-6 sm:px-10 grain">
        <Aurora />
        <div className="absolute inset-0 grid-bg opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="max-w-6xl mx-auto text-center relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-xs backdrop-blur-md bg-white/5 border border-white/10 rounded-full px-3 py-1.5 mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/80">Nebula v2.4 · Live</span>
              <span className="text-white/40">→</span>
              <span className="text-white/60">Scopri le novità</span>
            </div>
            <h1 className="text-6xl sm:text-7xl lg:text-[8.5vw] font-semibold tracking-[-0.04em] leading-[0.95] break-words">
              Automazione aziendale,<br />
              <span className="bg-gradient-to-r from-violet-300 via-cyan-200 to-pink-300 bg-clip-text text-transparent">guidata dall'AI.</span>
            </h1>
            <p className="mt-10 text-lg sm:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
              Nebula è la piattaforma AI che osserva, decide e agisce sui tuoi sistemi. Agenti autonomi che lavorano 24 ore su 24, con la stessa accountability di un team interno.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <a href="#pricing" className="group bg-white text-black px-6 py-3.5 rounded-xl font-medium hover:bg-white/90 transition flex items-center gap-2">Inizia gratis <span className="group-hover:translate-x-0.5 transition">→</span></a>
              <a href="#demo" className="backdrop-blur-md bg-white/5 border border-white/15 px-6 py-3.5 rounded-xl font-medium hover:bg-white/10 transition">Richiedi una demo</a>
            </div>
            <div className="mt-16 flex flex-wrap justify-center items-center gap-x-12 gap-y-4 text-white/30 text-sm">
              <span>Utilizzata da team in</span>
              {["Linear", "Vercel", "Notion", "Figma", "Stripe", "Anthropic"].map(b => <span key={b} className="font-medium text-white/50">{b}</span>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* LIVE METRICS */}
      <section className="px-6 sm:px-10 py-16 border-y border-white/5 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
          <LiveMetric v={2400000} suffix="+" label="Decisioni elaborate al giorno" />
          <LiveMetric v={99} suffix=".97%" label="SLA di uptime garantito" />
          <LiveMetric v={147} suffix=" ms" label="Latenza mediana" />
          <LiveMetric v={38} suffix="x" label="Più veloce di un team operativo" />
        </div>
      </section>

      {/* BENTO */}
      <section id="product" className="px-6 sm:px-10 py-32 relative">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <div className="text-xs tracking-[0.3em] uppercase text-violet-400 mb-4">— La piattaforma</div>
              <h2 className="text-5xl sm:text-7xl font-semibold tracking-[-0.03em] break-words">Soluzioni AI pronte alla produzione.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-6 grid-rows-[auto] gap-4">
            <Reveal className="lg:col-span-4">
              <div className="relative h-full rounded-3xl border border-white/10 bg-gradient-to-br from-violet-950/40 via-[#0a0c12] to-[#0a0c12] p-8 overflow-hidden min-h-[280px]">
                <div className="text-xs tracking-widest uppercase text-violet-400 mb-3">01 · Motore di ragionamento</div>
                <div className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Multi-agent reasoning, trasparente per default.</div>
                <p className="text-white/60 max-w-md">Ogni decisione lascia un audit trail completo. Ispeziona, riproduci e correggi in qualunque momento.</p>
                <div className="mt-8 font-mono text-xs space-y-1.5">
                  {["▸ analyze.intent → escalation_needed", "▸ retrieve.context (4 fonti)", "▸ plan.actions (3 step)", "✓ execute.send_reply"].map((l, i) => (
                    <div key={i} className="text-emerald-300/80">{l}</div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={80} className="lg:col-span-2">
              <div className="relative h-full rounded-3xl border border-white/10 bg-gradient-to-br from-cyan-950/40 via-[#0a0c12] to-[#0a0c12] p-8">
                <div className="text-xs tracking-widest uppercase text-cyan-300 mb-3">02 · Velocità</div>
                <div className="text-3xl font-semibold tracking-tight">147 ms</div>
                <div className="text-white/60 text-sm mt-2">Latenza end-to-end mediana</div>
                <div className="mt-8 h-24 flex items-end gap-1">
                  {[40, 65, 45, 80, 55, 90, 70, 95, 60, 85].map((h, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-cyan-400 to-cyan-400/20 rounded-sm" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={120} className="lg:col-span-2">
              <div className="relative h-full rounded-3xl border border-white/10 bg-gradient-to-br from-pink-950/30 via-[#0a0c12] to-[#0a0c12] p-8">
                <div className="text-xs tracking-widest uppercase text-pink-300 mb-3">03 · Integrazioni</div>
                <div className="text-2xl font-semibold tracking-tight mb-4">Oltre 120 connettori nativi</div>
                <div className="grid grid-cols-4 gap-2">
                  {["S", "L", "N", "G", "Z", "H", "P", "J"].map((l, i) => (
                    <div key={i} className="aspect-square rounded-lg bg-white/5 border border-white/10 grid place-items-center text-xs font-semibold">{l}</div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={160} className="lg:col-span-4">
              <div className="relative h-full rounded-3xl border border-white/10 bg-gradient-to-br from-[#0a0c12] to-violet-950/20 p-8">
                <div className="text-xs tracking-widest uppercase text-white/50 mb-3">04 · Compliance & sicurezza</div>
                <div className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">SOC 2 · ISO 27001 · GDPR · HIPAA</div>
                <p className="text-white/60 max-w-lg">Hosting esclusivo in datacenter svizzeri. Cifratura at-rest e in transito. Customer-managed keys disponibili sui piani Enterprise.</p>
                <div className="mt-6 flex gap-2 flex-wrap">
                  {["SOC2 Type II", "ISO 27001", "GDPR", "HIPAA", "FINMA-ready"].map(c => <span key={c} className="text-xs bg-white/5 border border-white/10 px-3 py-1 rounded-full">{c}</span>)}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTERACTIVE DASHBOARD */}
      <section id="demo" className="px-6 sm:px-10 py-32">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <div className="text-xs tracking-[0.3em] uppercase text-cyan-400 mb-4">— Demo interattiva</div>
              <h2 className="text-5xl sm:text-6xl font-semibold tracking-[-0.03em] break-words">Vedi Nebula in azione.</h2>
            </div>
          </Reveal>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent overflow-hidden shadow-2xl shadow-violet-900/20">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10 bg-white/[0.02]">
              <div className="flex gap-1.5"><div className="h-3 w-3 rounded-full bg-red-400/60" /><div className="h-3 w-3 rounded-full bg-yellow-400/60" /><div className="h-3 w-3 rounded-full bg-emerald-400/60" /></div>
              <div className="ml-4 text-xs text-white/40">nebula.ai/console</div>
            </div>
            <div className="grid lg:grid-cols-[200px_1fr] min-h-[420px]">
              <div className="border-r border-white/5 p-4 space-y-1">
                {[
                  { id: "requests" as const, l: "Richieste", c: "1.247" },
                  { id: "agents" as const, l: "Agenti", c: "12" },
                  { id: "analytics" as const, l: "Analytics", c: "" },
                ].map(tt => (
                  <button key={tt.id} onClick={() => setTab(tt.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex justify-between items-center ${tab === tt.id ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5"}`}>
                    <span>{tt.l}</span>{tt.c && <span className="text-[10px] text-white/40">{tt.c}</span>}
                  </button>
                ))}
              </div>
              <div className="p-6">
                {tab === "requests" && (
                  <div className="space-y-2">
                    {[
                      { t: "support.escalation.tier2", st: "completata", ms: 142 },
                      { t: "lead.qualify.enterprise", st: "in corso", ms: 89 },
                      { t: "invoice.dispute.resolve", st: "completata", ms: 311 },
                      { t: "schedule.calendar.sync", st: "completata", ms: 67 },
                      { t: "data.enrich.contact", st: "in coda", ms: 0 },
                    ].map((r, i) => (
                      <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 items-center px-3 py-3 rounded-lg hover:bg-white/[0.03]">
                        <div className="font-mono text-sm text-white/80">{r.t}</div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.st === "completata" ? "bg-emerald-400/10 text-emerald-300" : r.st === "in corso" ? "bg-cyan-400/10 text-cyan-300" : "bg-white/5 text-white/40"}`}>{r.st}</span>
                        <div className="text-xs text-white/40 tabular-nums w-16 text-right">{r.ms ? `${r.ms} ms` : "—"}</div>
                      </div>
                    ))}
                  </div>
                )}
                {tab === "agents" && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {["Triage supporto clienti", "Qualifica lead commerciali", "Bot finance & fatturazione", "Calendar AI"].map(a => (
                      <div key={a} className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                        <div className="flex justify-between items-center"><div className="font-medium text-sm">{a}</div><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /></div>
                        <div className="mt-3 text-xs text-white/50">Latenza media 124 ms · 99,94% successo</div>
                      </div>
                    ))}
                  </div>
                )}
                {tab === "analytics" && (
                  <div>
                    <div className="text-xs text-white/40 mb-3">Ultimi 7 giorni</div>
                    <div className="h-48 flex items-end gap-2">
                      {[60, 78, 55, 92, 70, 88, 95].map((h, i) => (
                        <div key={i} className="flex-1 bg-gradient-to-t from-violet-500 to-cyan-400 rounded-md" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-6 sm:px-10 py-32">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="text-xs tracking-[0.3em] uppercase text-pink-400 mb-4">— Prezzi</div>
              <h2 className="text-5xl sm:text-6xl font-semibold tracking-[-0.03em] break-words">Paghi solo per i risultati.</h2>
            </div>
            <div className="flex justify-center mb-12">
              <div className="bg-white/5 border border-white/10 rounded-full p-1 flex">
                <button onClick={() => setAnnual(false)} className={`px-5 py-2 rounded-full text-sm transition ${!annual ? "bg-white text-black" : "text-white/60"}`}>Mensile</button>
                <button onClick={() => setAnnual(true)} className={`px-5 py-2 rounded-full text-sm transition ${annual ? "bg-white text-black" : "text-white/60"}`}>Annuale <span className="text-emerald-300 text-xs">−20%</span></button>
              </div>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-4">
            {[
              { n: "Starter", p: annual ? 39 : 49, sub: "/mese", perks: ["10.000 decisioni al mese", "3 agenti attivi", "Supporto via email", "Accesso API completo"], cta: "Inizia gratis" },
              { n: "Scale", p: annual ? 239 : 299, sub: "/mese", perks: ["250.000 decisioni al mese", "Agenti illimitati", "Supporto prioritario", "Integrazioni custom", "Single Sign-On"], cta: "Prova 14 giorni", featured: true },
              { n: "Enterprise", p: 0, sub: "Su misura", perks: ["Volume illimitato", "SLA 99,99%", "Customer Success Manager dedicato", "Opzione on-premise", "Contratti personalizzati"], cta: "Parla con noi" },
            ].map((p) => (
              <Reveal key={p.n}>
                <div className={`relative h-full rounded-3xl border p-8 ${p.featured ? "border-violet-400/40 bg-gradient-to-br from-violet-950/40 to-[#0a0c12]" : "border-white/10 bg-white/[0.02]"}`}>
                  {p.featured && <div className="absolute -top-3 left-8 text-[10px] tracking-widest uppercase bg-gradient-to-r from-violet-400 to-cyan-400 text-black px-3 py-1 rounded-full font-semibold">Più scelto</div>}
                  <div className="text-sm text-white/60">{p.n}</div>
                  <div className="mt-3 text-5xl font-semibold tracking-tight">{p.p ? <>CHF {p.p}</> : "Su misura"}<span className="text-base text-white/50 font-normal ml-1">{p.sub}</span></div>
                  <ul className="mt-8 space-y-3 text-sm">
                    {p.perks.map(perk => <li key={perk} className="flex gap-3 text-white/80"><span className="text-emerald-400">✓</span>{perk}</li>)}
                  </ul>
                  <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Nebula AI — sono interessato al piano " + p.n)}`} className={`mt-10 block text-center py-3.5 rounded-xl font-medium transition ${p.featured ? "bg-white text-black hover:bg-white/90" : "bg-white/10 hover:bg-white/15"}`}>{p.cta}</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="px-6 sm:px-10 py-32 border-y border-white/5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8">
          {[
            { q: "Nebula ha sostituito sei full-time del nostro supporto. Risposte coerenti, sotto i 200 ms e con un audit trail perfetto.", n: "Anna L.", r: "VP Engineering · Fintech con sede a Zurigo" },
            { q: "L'ROI è arrivato in 27 giorni. È la prima piattaforma AI che siamo riusciti a portare in produzione senza un PoC infinito.", n: "Mark V.", r: "COO · SaaS B2B, Serie C" },
          ].map((t) => (
            <Reveal key={t.n}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10">
                <div className="text-3xl sm:text-4xl font-medium tracking-tight leading-[1.2]">"{t.q}"</div>
                <div className="mt-8 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-400 to-cyan-400 grid place-items-center text-sm font-bold text-black">{t.n[0]}</div>
                  <div>
                    <div className="font-medium">{t.n}</div>
                    <div className="text-sm text-white/50">{t.r}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 sm:px-10 py-32">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <h2 className="text-5xl sm:text-6xl font-semibold tracking-[-0.03em] text-center mb-16 break-words">Domande frequenti.</h2>
          </Reveal>
          <div className="space-y-2">
            {[
              { q: "I miei dati restano in Svizzera?", a: "Sì. Hosting esclusivo in datacenter svizzeri a Zurigo e Ginevra. Customer-managed keys disponibili sui piani Enterprise." },
              { q: "Quanto tempo serve per il deploy?", a: "Setup iniziale in 1–3 giorni. PoC completo entro 2 settimane con il nostro team di Solutions Engineering dedicato." },
              { q: "Quali modelli AI utilizzate?", a: "Architettura multi-LLM: OpenAI, Anthropic, Mistral e modelli proprietari fine-tuned. Routing automatico per ogni tipologia di task." },
              { q: "Posso provare la piattaforma gratis?", a: "Sì, 10.000 decisioni gratuite al mese sul piano Starter. Nessuna carta di credito richiesta." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 40}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition p-6">
                  <div className="flex justify-between items-center gap-4">
                    <div className="font-medium text-lg">{f.q}</div>
                    <div className={`text-2xl text-white/40 transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</div>
                  </div>
                  <div className={`overflow-hidden transition-all ${openFaq === i ? "max-h-40 mt-3" : "max-h-0"}`}>
                    <p className="text-white/60">{f.a}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="px-6 sm:px-10 py-32 relative">
        <Aurora />
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-6xl sm:text-8xl font-semibold tracking-[-0.04em] leading-[0.95] break-words">
            Smetti di osservare.<br />
            <span className="bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">Inizia a spedire.</span>
          </h2>
          <p className="mt-8 text-lg text-white/60 max-w-xl mx-auto">Setup in giornata. Primo agente in produzione entro 7 giorni. Cancellabile in qualsiasi momento, senza penali.</p>
          <div className="mt-12 flex justify-center gap-3 flex-wrap">
            <a href="#pricing" className="bg-white text-black px-8 py-4 rounded-xl font-medium hover:bg-white/90 transition">Inizia gratis</a>
            <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Vorrei richiedere una demo di Nebula AI")}`} className="backdrop-blur-md bg-white/5 border border-white/15 px-8 py-4 rounded-xl font-medium hover:bg-white/10 transition">Richiedi una demo</a>
          </div>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-12 border-t border-white/5 text-sm text-white/40">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-between gap-6">
          <div>© Nebula AI 2026 · Sede a Zurigo · Svizzera</div>
          <div>Demo FlashWeb · <Link to="/" className="underline text-white/70">flash-web.ch</Link></div>
        </div>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/demo/flashweb-ultimate")({
  head: () => ({
    meta: [
      { title: "Nebula AI — Automazione aziendale guidata dall'AI (Demo FlashWeb)" },
      { name: "description", content: "Piattaforma AI per l'automazione aziendale: agenti autonomi 24/7, deploy in giornata, hosting svizzero. Richiedi una demo." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: FlashWebUltimate,
});
